import type { InvoiceTemplate, ItemsTableColumnConfig, TemplateElement } from "./types";

const now = new Date("2026-06-21T00:00:00.000Z");

function field(
  id: string,
  slot: TemplateElement["slot"],
  x: number,
  y: number,
  w: number,
  h: number,
  fontSize = 10,
  fontWeight: TemplateElement["style"]["fontWeight"] = "regular",
): TemplateElement {
  return {
    id,
    type: "field",
    slot,
    x,
    y,
    w,
    h,
    style: { fontSize, fontWeight, color: "#222222" },
  };
}

function staticText(
  id: string,
  text: string,
  x: number,
  y: number,
  w: number,
  h: number,
  fontSize = 10,
  fontWeight: TemplateElement["style"]["fontWeight"] = "regular",
  color = "#222222",
): TemplateElement {
  return {
    id,
    type: "static_text",
    x,
    y,
    w,
    h,
    text,
    style: { fontSize, fontWeight, color },
  };
}

function divider(
  id: string,
  x: number,
  y: number,
  w: number,
  borderColor = "#D6D3CE",
  borderWidth = 0.5,
): TemplateElement {
  return {
    id,
    type: "divider",
    x,
    y,
    w,
    h: 0,
    style: { borderColor, borderWidth },
  };
}

export const DEFAULT_ITEMS_TABLE_COLUMNS = [
  { key: "description", label: "Description", width: 62, align: "left" },
  { key: "quantity", label: "Qty", width: 12, align: "right" },
  { key: "unit", label: "Unit", width: 14, align: "left" },
  { key: "unitPrice", label: "Unit price", width: 25, align: "right" },
  { key: "taxRate", label: "Tax", width: 17, align: "right" },
  { key: "lineTax", label: "Tax amount", width: 20, align: "right" },
  { key: "lineNet", label: "Line net", width: 24, align: "right" },
] satisfies ItemsTableColumnConfig[];

export const BUILT_IN_TEMPLATES: InvoiceTemplate[] = [
  {
    id: "builtin-clean-a4",
    name: "Clean A4",
    pageSize: "a4",
    builtIn: true,
    access: "free",
    description: "Simple, dependable layout for everyday invoice documents.",
    createdAt: now,
    updatedAt: now,
    elements: [
      {
        id: "title",
        type: "static_text",
        x: 18,
        y: 18,
        w: 80,
        h: 12,
        text: "INVOICE",
        style: { fontSize: 24, fontWeight: "bold", color: "#111111" },
      },
      field("invoice-number", "invoice_number", 128, 20, 64, 8, 13, "bold"),
      field("issue-date", "issue_date", 128, 33, 64, 7),
      field("due-date", "due_date", 128, 43, 64, 7),
      field("seller-name", "seller_name", 18, 47, 78, 7, 11, "bold"),
      field("seller-address", "seller_address", 18, 57, 78, 23),
      field("seller-tax-id", "seller_tax_id", 18, 82, 78, 7),
      {
        id: "bill-to-label",
        type: "static_text",
        x: 18,
        y: 101,
        w: 40,
        h: 6,
        text: "BILL TO",
        style: { fontSize: 8, fontWeight: "bold", color: "#777777" },
      },
      field("buyer-name", "buyer_name", 18, 111, 120, 13, 11, "bold"),
      field("buyer-address", "buyer_address", 18, 126, 120, 22),
      field("buyer-tax-id", "buyer_tax_id", 18, 152, 120, 7),
      {
        id: "header-line",
        type: "divider",
        x: 18,
        y: 173,
        w: 174,
        h: 0,
        style: { borderColor: "#D6D3CE", borderWidth: 0.5 },
      },
      {
        id: "items",
        type: "items_table",
        x: 18,
        y: 179,
        w: 174,
        h: 62,
        style: {
          fontSize: 8.5,
          color: "#222222",
          borderColor: "#D6D3CE",
          backgroundColor: "#F7F5F1",
        },
        table: {
          columns: DEFAULT_ITEMS_TABLE_COLUMNS,
          headerHeight: 8,
          rowHeight: 14,
          showBorders: true,
        },
      },
      field("subtotal", "subtotal", 128, 241, 64, 7, 10),
      field("tax-breakdown", "tax_breakdown", 128, 252, 64, 14, 9),
      field("total", "total", 128, 272, 64, 9, 15, "bold"),
      field("payment-terms", "payment_terms", 18, 242, 74, 14),
      field("notes", "notes", 18, 262, 74, 20),
    ],
  },
  {
    id: "premium-editorial-ledger",
    name: "Editorial Ledger",
    pageSize: "a4",
    builtIn: true,
    access: "premium",
    description: "A refined editorial layout for consultants, studios, and premium service work.",
    createdAt: now,
    updatedAt: now,
    elements: [
      staticText("title", "INVOICE", 18, 17, 78, 13, 22, "bold", "#1F2937"),
      {
        id: "brand-band",
        type: "divider",
        x: 18,
        y: 35,
        w: 174,
        h: 0,
        style: { borderColor: "#0F766E", borderWidth: 1.2, brandBorderToken: "accent_line" },
      },
      field("seller-name", "seller_name", 18, 45, 78, 8, 11, "bold"),
      field("seller-address", "seller_address", 18, 56, 78, 22, 8.5),
      field("seller-tax-id", "seller_tax_id", 18, 81, 78, 7, 8.5),
      field("invoice-number", "invoice_number", 126, 45, 66, 9, 13, "bold"),
      field("issue-date", "issue_date", 126, 60, 66, 7, 9),
      field("due-date", "due_date", 126, 71, 66, 7, 9),
      staticText("bill-to-label", "BILL TO", 18, 103, 48, 6, 8, "bold", "#64748B"),
      field("buyer-name", "buyer_name", 18, 113, 86, 10, 11, "bold"),
      field("buyer-address", "buyer_address", 18, 127, 86, 22, 8.5),
      field("buyer-tax-id", "buyer_tax_id", 18, 153, 86, 7, 8.5),
      {
        id: "logo",
        type: "logo",
        x: 130,
        y: 101,
        w: 42,
        h: 23,
        style: { borderColor: "#CBD5E1", borderWidth: 0.5, brandBorderToken: "accent_line" },
      },
      divider("table-line", 18, 172, 174, "#94A3B8", 0.6),
      {
        id: "items",
        type: "items_table",
        x: 18,
        y: 179,
        w: 174,
        h: 62,
        style: {
          fontSize: 8.5,
          color: "#1F2937",
          borderColor: "#CBD5E1",
          backgroundColor: "#ECFDF5",
          brandBackgroundToken: "accent_soft",
          brandBorderToken: "accent_line",
        },
        table: {
          columns: DEFAULT_ITEMS_TABLE_COLUMNS,
          headerHeight: 8,
          rowHeight: 14,
          showBorders: true,
        },
      },
      field("payment-terms", "payment_terms", 18, 245, 74, 14, 8.5),
      field("notes", "notes", 18, 264, 74, 18, 8.5),
      field("subtotal", "subtotal", 128, 243, 64, 7, 10),
      field("tax-breakdown", "tax_breakdown", 128, 254, 64, 14, 9),
      field("total", "total", 128, 274, 64, 9, 16, "bold"),
    ],
  },
  {
    id: "premium-compact-brand",
    name: "Compact Brand",
    pageSize: "a4",
    builtIn: true,
    access: "premium",
    description: "A dense branded layout for repeat invoices and batch output.",
    createdAt: now,
    updatedAt: now,
    elements: [
      {
        id: "header-block",
        type: "static_text",
        x: 0,
        y: 0,
        w: 210,
        h: 39,
        text: "",
        style: { backgroundColor: "#F1F5F9", brandBackgroundToken: "accent_soft" },
      },
      staticText("title", "INVOICE", 18, 15, 62, 12, 20, "bold", "#111827"),
      field("invoice-number", "invoice_number", 128, 15, 64, 8, 13, "bold"),
      field("issue-date", "issue_date", 128, 27, 64, 6, 8.5),
      field("seller-name", "seller_name", 18, 51, 75, 8, 11, "bold"),
      field("seller-address", "seller_address", 18, 62, 75, 20, 8),
      field("seller-tax-id", "seller_tax_id", 18, 85, 75, 7, 8),
      field("buyer-name", "buyer_name", 112, 51, 80, 8, 11, "bold"),
      field("buyer-address", "buyer_address", 112, 62, 80, 20, 8),
      field("buyer-tax-id", "buyer_tax_id", 112, 85, 80, 7, 8),
      divider("divider", 18, 104, 174, "#334155", 0.8),
      {
        id: "items",
        type: "items_table",
        x: 18,
        y: 114,
        w: 174,
        h: 96,
        style: {
          fontSize: 8,
          color: "#111827",
          borderColor: "#CBD5E1",
          backgroundColor: "#F8FAFC",
        },
        table: {
          columns: DEFAULT_ITEMS_TABLE_COLUMNS,
          headerHeight: 8,
          rowHeight: 10,
          showBorders: true,
        },
      },
      field("payment-terms", "payment_terms", 18, 224, 72, 16, 8),
      field("notes", "notes", 18, 245, 72, 22, 8),
      field("subtotal", "subtotal", 128, 224, 64, 7, 9),
      field("tax-breakdown", "tax_breakdown", 128, 235, 64, 14, 8),
      field("total", "total", 128, 257, 64, 9, 15, "bold"),
      {
        id: "signature",
        type: "signature",
        x: 18,
        y: 272,
        w: 54,
        h: 12,
        style: { fontSize: 11, brandColorToken: "accent", brandFontToken: "title" },
      },
    ],
  },
];

export function getBuiltInTemplate(id: string): InvoiceTemplate | undefined {
  return BUILT_IN_TEMPLATES.find((template) => template.id === id);
}
